import { useState, useEffect } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { publicApi } from "../lib/api";
import { Button } from "../components/ui/button";
import { CheckCircle, ArrowRight, Clock } from "lucide-react";

export default function RentalConfirmationPage() {
    const { bookingId } = useParams();
    const [searchParams] = useSearchParams();
    const [booking, setBooking] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const checkStatus = async () => {
            try {
                const statusRes = await publicApi.getBookingStatus(bookingId);
                if (statusRes.data.payment_status === "paid") {
                    const bookingRes = await publicApi.getBooking(bookingId);
                    setBooking(bookingRes.data);
                    setLoading(false);
                } else {
                    // Keep polling
                    setTimeout(checkStatus, 2000);
                }
            } catch {
                setTimeout(checkStatus, 3000);
            }
        };
        checkStatus();
    }, [bookingId]);

    if (loading) {
        return (
            <div className="min-h-[70vh] flex items-center justify-center" data-testid="rental-confirmation-loading">
                <div className="text-center">
                    <Clock className="h-12 w-12 text-primary mx-auto mb-4 animate-pulse" />
                    <h2 className="font-heading text-xl font-bold text-foreground mb-2">Processing your deposit...</h2>
                    <p className="text-muted-foreground text-sm">Please wait while we confirm your payment.</p>
                </div>
            </div>
        );
    }

    const pricing = booking?.pricing || {};

    return (
        <div className="min-h-[70vh]" data-testid="rental-confirmation-page">
            <div className="container mx-auto px-6 lg:px-12 py-16 lg:py-24 max-w-3xl">
                <div className="text-center mb-10">
                    <div className="w-20 h-20 bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto mb-6">
                        <CheckCircle className="h-10 w-10 text-primary" />
                    </div>
                    <h1 className="font-heading text-4xl sm:text-5xl font-black text-foreground tracking-tight mb-3">
                        Rental Confirmed!
                    </h1>
                    <p className="text-muted-foreground text-base">
                        Your dry ice blaster is reserved and ready for your dates.
                    </p>
                    <p className="text-foreground font-mono text-sm mt-2">
                        Booking #: <span className="text-primary font-bold">{booking?.booking_number}</span>
                    </p>
                </div>

                <div className="bg-card border border-border p-6 mb-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                            <p className="text-muted-foreground text-xs uppercase">Machine</p>
                            <p className="text-foreground font-bold">{booking?.machine_name}</p>
                        </div>
                        <div>
                            <p className="text-muted-foreground text-xs uppercase">Fulfillment</p>
                            <p className="text-foreground font-bold">{(booking?.fulfillment || "pickup").replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())}</p>
                        </div>
                        <div>
                            <p className="text-muted-foreground text-xs uppercase">Start Date</p>
                            <p className="text-foreground font-bold">{booking?.start_date?.slice(0, 10)}</p>
                        </div>
                        <div>
                            <p className="text-muted-foreground text-xs uppercase">End Date</p>
                            <p className="text-foreground font-bold">{booking?.end_date?.slice(0, 10)}</p>
                        </div>
                    </div>

                    <div className="border-t border-border pt-4 space-y-2 text-sm">
                        <div className="flex justify-between"><span className="text-muted-foreground">Rental ({pricing.days} days)</span><span>${pricing.subtotal?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span></div>
                        <div className="flex justify-between text-green-500"><span>Deposit Paid</span><span>-${pricing.deposit?.toFixed(2)}</span></div>
                        <div className="border-t border-border pt-2 flex justify-between font-bold text-lg"><span>Balance Due</span><span className="text-primary">${pricing.balance?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span></div>
                    </div>
                </div>

                <div className="bg-card border border-border p-6 mb-6">
                    <h2 className="font-heading font-bold text-foreground mb-3">What's Next</h2>
                    <ul className="space-y-3 text-sm text-muted-foreground">
                        <li className="flex gap-3"><span className="w-6 h-6 bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold shrink-0">1</span> A confirmation email with your rental agreement has been sent to your email.</li>
                        <li className="flex gap-3"><span className="w-6 h-6 bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold shrink-0">2</span> {booking?.fulfillment === "pickup" ? "Bring a valid ID to our Apex, NC facility on your start date." : "Our team will coordinate delivery details with you."}</li>
                        <li className="flex gap-3"><span className="w-6 h-6 bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold shrink-0">3</span> Remaining balance of ${pricing.balance?.toLocaleString('en-US', { minimumFractionDigits: 2 })} can be paid online or at pickup.</li>
                    </ul>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Link to="/rent">
                        <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase font-bold tracking-wider px-8">
                            Browse More Rentals <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                    </Link>
                    <Link to="/">
                        <Button variant="outline" className="rounded-none uppercase font-bold tracking-wider px-8 border-2">Back to Home</Button>
                    </Link>
                </div>
            </div>
        </div>
    );
}
